<template>
  <section class="login router view" :class="['ui basic inverted segment ' + colorClass]">
    <div class="ui large label env" :class="[env.color]" v-if="env && env.name">
      <i :class="[`${env.icon} icon`]" v-if="env.icon" />
      {{ env.name }}
    </div>

    <transition
      appear
      enter-active-class="animate__animated animate__fadeInDown"
      leave-active-class="animate__animated animate__fadeOutUp"
      mode="out-in"
    >
      <img class="item nav logo" :src="logo" width="180" :alt="product" />
    </transition>

    <template v-if="!tokenVerified || authenticationFailed || error">
      <h2>{{ $t('ENTER_CUESTA', { productName: product || 'Cuesta' }) }}</h2>

      <div class="ui negative message" v-if="error">
        <div class="header">{{ $t(error + '_HEADER') }}</div>
        <p>{{ $t(error + '_HEADER') }}</p>
      </div>

      <transition
        appear
        enter-active-class="animate__animated animate__fadeInUp"
        leave-active-class="animate__animated animate__fadeOutDown"
        mode="out-in"
      >
        <form class="ui inverted form" @submit.prevent="login" autocomplete="off">
          <transition
            enter-active-class="animate__animated animate__fadeInLeft"
            leave-active-class="animate__animated animate__fadeOutRight"
            mode="out-in"
          >
            <div
              class="ui fieldset"
              :class="{ 'animate__animated animate__headShake animate__slower': loginError }"
              v-if="step == 'credentials'"
              key="credentials"
            >
              <div class="field">
                <input
                  id="userName"
                  type="text"
                  autocomplete="username"
                  :placeholder="$t('USERNAME')"
                  :disabled="user.persistent"
                  v-model="user.name"
                  required
                />
              </div>

              <div class="field">
                <input
                  id="userPassword"
                  type="password"
                  autocomplete="current-password"
                  :placeholder="$t('PASSWORD')"
                  :disabled="user.persistent"
                  v-model="user.password"
                  required
                />
              </div>
            </div>
            <!-- if user hasn't enabled totp and totp is strictly enforced then we need to show enablement actions -->
            <div
              class="ui fieldset"
              key="enableAndRecovery"
              v-else-if="step == 'enable' || step == 'recoveryCodes'"
            >
              <two-factor
                :login="false"
                @totp="onEnable"
                :info="twoFactorInfo"
                :recoveryCodes="twoFactorRecovery"
              />
            </div>
            <div class="ui fieldset" key="totp" v-else>
              <two-factor :login="true" @totp="onTotp" />
            </div>
          </transition>

          <button
            class="ui right labeled inverted icon button large"
            :class="{ disabled: (step === 'totp' || step === 'recoveryCodes') && !totpCode }"
          >
            {{ $t(loginButtonI18n[step]) }}
            <i class="unlock alternate icon"></i>
          </button>
        </form>
      </transition>
    </template>

    <div class="info">
      <div class="version">
        <div class="component">Cuesta</div>
        <div class="v" v-if="version.cuesta">{{ version.cuesta }}</div>
        <div class="ui active inline slow mini loader" v-else />
      </div>

      <div class="version">
        <div class="component">Kwanza</div>
        <div class="v" v-if="version.kwanza">{{ version.kwanza }}</div>
        <div class="ui active inline slow mini loader" v-else />
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';

// Hack: Pump up the rule priority with multiple classes
section.login.router.view {
  position: fixed;
  width: 100vw;
  height: 100vh;
  left: 0;
  top: 0;
  padding-left: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.info {
  width: 300px;
  position: fixed;
  bottom: 0px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: center;

  div {
    display: inline-block;
  }

  .version {
    padding: 5px;
    font-size: 0.8em;
    .component {
      font-weight: bold;
      padding-right: 5px;
    }
  }
}

.fieldset {
  margin-bottom: 20px;
}

.label.env {
  position: fixed;
  top: 10px;
  margin: auto;
}

.logged {
  margin-top: $gs * 2;
  text-align: center;
}

.ui.form {
  max-width: 300px;

  .field {
    &:nth-child(1) {
      margin-bottom: -1px;

      input {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    &:nth-child(2) {
      input {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
      }
    }
  }
}

.ui.button {
  width: 100%;
}
</style>

<script>
// @flow
import { Auth, Toast } from 'services'
import Vue from 'vue'
import jwtDecode from 'jwt-decode'
import { HttpErrorEnum } from 'enums'
import { MutationTypes } from 'store'
import TwoFactor from '../Users/TwoFactor'

export default {
  name: 'login',

  props: {
    error: {
      type: String,
      default: () => ''
    },
    intendedRoute: {
      type: Object,
      default: () => null
    }
  },

  components: { TwoFactor },

  data() {
    return {
      loginError: null,
      tokenVerified: false,
      authenticationFailed: false,
      step: 'credentials',
      twoFactorInfo: {},
      twoFactorRecovery: null,
      totpCode: null,
      decodedToken: null,
      loginButtonI18n: {
        credentials: 'LOGIN',
        enable: '2FA_ENABLE_AND_VERIFY_CODE',
        totp: '2FA_VERIFY_CODE',
        recoveryCodes: '2FA_RECOVERY_CODES_PROCEED'
      },

      user: {
        name: '',
        password: '',
        persistent: false
      },

      defaultLogo:
        'data:image/svg+xml;base64,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'
    }
  },

  computed: {
    version() {
      return this.$store.state.config.version
    },
    env() {
      return this.$store.state.config.env
    },

    colorClass() {
      return this.$store.state.config.color
    },
    product() {
      const n = this.$i18n.t('PRODUCT_NAME')
      // if empty or default value
      return !n || n === 'PRODUCT_NAME' ? '' : n
    },
    logo() {
      const l = this.$i18n.t('PRODUCT_LOGO')
      // if empty or default value
      if (!l || l === 'PRODUCT_LOGO') return this.defaultLogo
      // append data:image... if it isnt alread there
      return l.indexOf('data:') === 0 ? l : 'data:image/png;base64,' + l
    }
  },

  methods: {
    authenticated(token, proceed) {
      Auth.saveToken(token, this.user.persistent)
      Auth.saveCredentials(this.user.name, this.user.password)
      this.$ability.update(Auth.extractRulesFromToken(token))
      this.$store.commit(MutationTypes.USER_AUTHENTICATED, this.user.name)
    },

    proceed() {
      this.$router.push(this.intendedRoute || { name: 'root' })
    },
    onTotp(code) {
      this.totpCode = code
    },
    onEnable(code) {
      this.totpCode = code
    },
    login() {
      this.loginError = null

      if (this.step === 'credentials') {
        Auth.authenticate(this.user.name, this.user.password)
          .then(({ token, two_factor, two_factor_required }) => {
            if (two_factor) {
              // We'll save the intermediate token for use with TOTP code submission
              Auth.setAuthorizationHeader(token)
              this.step = 'totp'
              console.debug('2FA token saved')
            } else if (!two_factor && two_factor_required) {
              // We'll save the intermediate token for use with TOTP code submission
              Auth.setAuthorizationHeader(token)
              // request info (qr + secret)
              Auth.twoFactor.init().then(info => {
                this.twoFactorInfo = info
              })
              this.step = 'enable'
            } else {
              this.authenticated(token)
              this.proceed()
            }
          })
          .catch(err => {
            this.loginError = err
            console.error('Login error', HttpErrorEnum.toString(err))
            if (err === HttpErrorEnum.SERVER_ERROR) {
              Toast.error(this.$i18n.t('ERR', { action: this.$i18n.t('AUTHENTICATE') }), {
                remove: 10000,
                text: this.$i18n.t('SERVER_ERROR')
              })
            } else if (err === HttpErrorEnum.UNAUTHORIZED) {
              Toast.error(this.$i18n.t('ERR', { action: this.$i18n.t('AUTHENTICATE') }), {
                remove: 10000,
                text: this.$i18n.t('AUTHENTICATE_TIP')
              })
            } else {
              Toast.error(this.$i18n.t('ERR', { action: this.$i18n.t('AUTHENTICATE') }), {
                remove: 10000,
                text: this.$i18n.t('UNKNOWN_ERROR')
              })
            }
          })
      } else if (this.step == 'enable') {
        Auth.twoFactor
          .enable(this.twoFactorInfo.secret, this.totpCode)
          .then(r => {
            this.twoFactorRecovery = r.recovery_codes
            this.step = 'recoveryCodes'
            // actually login and save credentials but do not proceed to home
            this.authenticated(r.token)
          })
          .catch(err => {
            console.error('2FA enablement phase failed', err)
            this.twoFactorInfo = {}
            this.totpCode = ''
            this.step = 'credentials'
          })
      } else if (this.step == 'recoveryCodes') {
        // proceed to home or whatever
        this.proceed()
      } else {
        // 2FA
        // we cannot make this request w/o a token, we need one from the initial request
        Auth.twoFactor
          .verify(this.totpCode)
          .then(r => {
            console.debug('2FA validated', r)
            this.authenticated(r.token)
            this.proceed()
          })
          .catch(err => {
            console.error('2FA verify failed', err)
            this.loginError = err
            this.step = 'credentials'
            this.totpCode = null
            Auth.setAuthorizationHeader('')
            Toast.error(this.$i18n.t('ERR', { action: this.$i18n.t('AUTHENTICATE') }), {
              remove: 10000,
              text: this.$i18n.t('2FA_TOTP_CODE_VALIDATION_FAILED')
            })
          })
      }
    },

    home() {
      this.$router.push({ name: 'root' })
    }
  },

  mounted() {
    if (this.$route && this.$route.query && this.$route.query.status === 401)
      this.authenticationFailed = true

    const token = Auth.getToken()

    if (token) {
      this.decodedToken = jwtDecode(token)
      this.tokenVerified = Auth.checkToken(token)
      if (this.tokenVerified) this.home()
    }

    // We'll try to login automatically if user and pass are supplied as query params
    if (this.$route && this.$route.query) {
      if (this.$route.query.user && this.$route.query.password) {
        this.user.name = this.$route.query.user
        this.user.password = this.$route.query.password
        this.user.persistent = true
        this.login()
      }
    }
  }
}
</script>
